import { useCallback, useMemo, useState } from 'react'

import type { PaginationState } from '@tanstack/react-table'

import {
  DateRangeContextKey,
  useDateRangeContext,
} from '@/components/provider/date-range-provider.tsx'
import { CalendarRangeDatePicker } from '@/components/ui/calendar/calendar-range-date-picker.tsx'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable'
import { WithRefresher } from '@/components/ui/with-refresher'
import { useAutoFollowCaption } from '@/modules/automations/hooks/use-auto-follow-caption.ts'
import { classNames } from '@/utils/helpers'

import type {
  AutoFollowTableKeys,
  TableSelectionType,
} from '../hooks/use-auto-follow-table'
import {
  tableSelectionVariants,
  useAutoFollowTable,
} from '../hooks/use-auto-follow-table'
import { useBuildAutoFollowColumns } from '../hooks/use-build-auto-follow-columns'

export const AutoFollowTable = () => {
  const { date, setDate } = useDateRangeContext(DateRangeContextKey.AutoFollow)

  const [selectedTable, setSelectedTable] = useState<TableSelectionType>(
    tableSelectionVariants[0],
  )

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const {
    data,
    isFetching: isFetchingTableData,
    totalPages,
    refetch: refetchTableData,
  } = useAutoFollowTable({
    page: pagination.pageIndex,
    size: pagination.pageSize,
    dateRange: date,
    key: selectedTable.key,
  })

  const {
    data: captionData,
    isFetching: isFetchingCaption,
    refetch: refetchCaption,
  } = useAutoFollowCaption({
    dateRange: date,
  })

  const isFetching = useMemo(
    () => isFetchingTableData || isFetchingCaption,
    [isFetchingTableData, isFetchingCaption],
  )
  const refetch = useCallback(() => {
    refetchTableData()
    refetchCaption()
  }, [refetchTableData, refetchCaption])

  const memoizedPaginationState = useMemo(
    () => ({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
    }),
    [pagination.pageIndex, pagination.pageSize],
  )

  const columns = useBuildAutoFollowColumns(data, selectedTable.key)

  const handleTableChange = useCallback(
    (value: AutoFollowTableKeys) => {
      const selectedTableData = tableSelectionVariants.find(
        (table) => table.key === value,
      )

      if (selectedTable) {
        setPagination({ pageIndex: 0, pageSize: 10 })
      }

      setSelectedTable((table) => selectedTableData || table)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const title = (
    <div>
      <Select
        onValueChange={handleTableChange}
        defaultValue={selectedTable.key}
      >
        <SelectTrigger className="w-fit border-none bg-inherit p-0 text-xl font-semibold text-title">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {tableSelectionVariants.map((variant) => (
            <SelectItem
              key={variant.key}
              value={variant.key}
              className={classNames({
                'bg-primary-500 text-white hover:bg-primary-300':
                  selectedTable.key === variant.key,
              })}
            >
              {variant.displayName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-sm font-normal text-text-sub">
        {selectedTable.description}
      </span>
    </div>
  )

  const dateFilter = (
    <WithRefresher
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <CalendarRangeDatePicker
        date={date}
        setDate={setDate}
      />
    </WithRefresher>
  )

  return (
    <div className="grid py-8">
      {data && !isFetching && (
        <DataTable
          key={selectedTable.key}
          title={title}
          enablePagination
          enableSorting
          paginationState={memoizedPaginationState}
          onPaginationChange={setPagination}
          columns={columns}
          data={data.fans}
          pageCount={totalPages}
          extra={dateFilter}
          footer={
            <span className="text-sm font-normal text-text-sub">
              Followed {captionData?.followed}/
              {captionData!.queued +
                captionData!.failed +
                captionData!.followed}
            </span>
          }
        />
      )}
      {isFetching && <Skeleton className="h-60 w-full" />}
    </div>
  )
}
